
import { Options, prop, Vue } from "vue-class-component";
import {
  ChargeableWeightBreakdownV4,
  ProtectionPackingBookingV4
} from "@/domain/entities/BookingV4";
import ChevronDown from "@/app/ui/assets/svg/chevron-down-new.svg";
import ChevronUp from "@/app/ui/assets/svg/chevron-up-new.svg";
import OverlayPanel from "primevue/overlaypanel";

class Props {
  chargeableWeightKg = prop<number>({ required: true, type: Number });
  totalKoli = prop<number>({ required: true, type: Number });
  chargeableWeightExpanded = prop<boolean>({ required: true, type: Boolean });
  chargeableWeightBreakdown = prop<ChargeableWeightBreakdownV4>({
    required: true,
    type: ChargeableWeightBreakdownV4
  });
  woodpacking = prop<boolean>({ required: true, type: Boolean });
  numericLocale = prop<string>({ default: "" });
}

interface OverlayPanelRef {
  container: HTMLElement | null;
  show(event: { currentTarget: HTMLElement }): void;
  hide(): void;
}

@Options({
  name: "ChargeableWeightSummaryV4",
  components: { OverlayPanel },
  emits: ["update:protectionPacking", "update:chargeableWeightExpanded"],
  watch: {
    chargeableWeightExpanded: "syncOverlayVisibility"
  }
})
class ChargeableWeightSummaryV4 extends Vue.with(Props) {
  alignmentFrame = 0;

  get chargeableWeightOverlay(): OverlayPanelRef {
    return this.$refs.chargeableWeightOverlay as OverlayPanelRef;
  }

  get chevronIcon(): string {
    return this.chargeableWeightExpanded ? ChevronUp : ChevronDown;
  }

  mounted(): void {
    document.addEventListener("click", this.onDocumentClick);
    this.syncOverlayVisibility(this.chargeableWeightExpanded);
  }

  beforeUnmount(): void {
    document.removeEventListener("click", this.onDocumentClick);
    window.cancelAnimationFrame(this.alignmentFrame);
  }

  get totalGrossWeightKg(): number {
    return this.chargeableWeightBreakdown.totalGrossWeightKg;
  }

  get totalVolumeWeightKg(): number {
    return this.chargeableWeightBreakdown.totalVolumeWeightKg;
  }

  get isGrossWeightDominant(): boolean {
    return this.totalGrossWeightKg >= this.totalVolumeWeightKg;
  }

  get isVolumeWeightDominant(): boolean {
    return this.totalVolumeWeightKg > this.totalGrossWeightKg;
  }

  get displayedWoodpackingWeightKg(): number {
    return this.woodpacking
      ? this.chargeableWeightBreakdown.totalWpWeightKg
      : 0;
  }

  get totalKoliLabel(): string {
    return this.$i18n.locale === "id" ? "Total koli" : "Total packages";
  }

  get weightDetailTitle(): string {
    return this.$i18n.locale === "id"
      ? "Detail berat barang"
      : "Item weight detail";
  }

  get volumeWeightLabel(): string {
    return this.$i18n.locale === "id"
      ? "Total berat dimensi"
      : "Total volume weight";
  }

  get woodpackingWeightLabel(): string {
    return this.$i18n.locale === "id"
      ? "Total berat tambah packing kayu"
      : "Total wood packing additional weight";
  }

  get finalWeightLabel(): string {
    return this.$i18n.locale === "id"
      ? "Berat dikenakan biaya"
      : "Chargeable weight";
  }

  onToggleExpand(): void {
    this.emitProtectionPacking(!this.chargeableWeightExpanded);
  }

  syncOverlayVisibility(expanded: boolean): void {
    if (!expanded) {
      this.chargeableWeightOverlay.hide();
      return;
    }

    const target = this.$refs.chargeableWeightTarget as HTMLElement;
    this.chargeableWeightOverlay.show({ currentTarget: target });
    this.$nextTick(() => {
      window.cancelAnimationFrame(this.alignmentFrame);
      this.alignmentFrame = window.requestAnimationFrame(() =>
        this.alignOverlayToBottomLeft(target)
      );
    });
  }

  alignOverlayToBottomLeft(target: HTMLElement): void {
    const overlay = this.chargeableWeightOverlay.container;
    if (!overlay) return;

    const targetRect = target.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    const left = targetRect.left - overlay.offsetWidth;


    // const horizontalAdjustment = targetRect.left - overlayRect.right - 8;
    const verticalAdjustment = targetRect.bottom - overlayRect.bottom;
    // overlay.style.left = `${overlay.offsetLeft + horizontalAdjustment}px`;
    overlay.style.left = `${Math.max(16, left) + window.pageXOffset}px`;
    overlay.style.top = `${overlay.offsetTop + verticalAdjustment}px`;
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.chargeableWeightExpanded) return;

    const target = event.target as Node;
    const overlay = this.chargeableWeightOverlay.container;
    if (
      (this.$el as HTMLElement).contains(target) ||
      overlay?.contains(target)
    ) {
      return;
    }

    this.emitProtectionPacking(false);
  }

  emitProtectionPacking(chargeableWeightExpanded: boolean): void {
    this.$emit(
      "update:protectionPacking",
      new ProtectionPackingBookingV4({
        chargeableWeightKg: this.chargeableWeightKg,
        totalKoli: this.totalKoli,
        chargeableWeightExpanded,
        woodpacking: this.woodpacking
      })
    );
  }

  formatWeight(value: number): string {
    const locale = this.$i18n.locale === "id" ? "id-ID" : "en-US";
    const currency = this.numericLocale
      ? this.numericLocale === "id"
        ? "id-ID"
        : "en-US"
      : locale;
    return new Intl.NumberFormat(currency, {
      maximumFractionDigits: 2
    }).format(Number.isFinite(value) ? value : 0);
  }

  onKeydownTrigger(e: KeyboardEvent) {
    if (e.key === "Escape" || e.key === "ArrowDown") {
      this.$emit("update:chargeableWeightExpanded", false);
      return;
    }

    if (e.key === "ArrowUp") {
      this.$emit("update:chargeableWeightExpanded", false);
      return;
    }
  }
}

export default ChargeableWeightSummaryV4;
